import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { InfiniteScrollCustomEvent } from '@ionic/angular';
import { filter, finalize, interval, Subject, takeUntil, throttle } from 'rxjs';
import { Order, OrdersResultDto } from '../../dto/order.dto';
import { OrdersService } from '../../services/order/orders.service';
import { OrdersListDto } from '../../dto/orders-list.dto';
import { MenuTabChangeService } from '../../services/menu-tab-change.service';
import { Status } from '../../dto/status.enum';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss']
})
export class OrdersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() refreshData: string;

  @Output() totalCount = new EventEmitter<number>();

  @Input() ordersListData: OrdersListDto;

  @Input() tabName: string;

  ordersItems: Order[] = [];
  limit: number = 10;
  loading: boolean = false;
  noData: boolean = false;
  isNoMoreData: boolean;
  isLoadedLess: boolean;
  isRefresherDisabled: boolean;

  private loadingIds: string = '';
  private offset: number = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly reloadList$ = new Subject<void>();

  constructor(
    private ordersService: OrdersService,
    private tabChangeService: MenuTabChangeService
  ) {
    this.reloadList$
      .pipe(
        throttle(() => interval(200)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.reloadList());
  }

  ngOnInit(): void {
    this.tabChangeService.refreshData
      .pipe(
        filter(curTabName => this.tabName === curTabName),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.ordersItems = [];
        this.loading = true;
        this.isRefresherDisabled = true;
        this.reloadList$.next();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['ordersListData']?.currentValue?.searchTerms !==
      changes['ordersListData']?.previousValue?.searchTerms
    ) {
      const { searchTerms } = this.ordersListData;
      if (searchTerms) {
        this.loadingIds = searchTerms
          .map(obj => `${obj.region.id}|${obj.district.id}`)
          .join();
      } else {
        this.loadingIds = '';
      }
    }
    this.reloadList$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onIonInfinite(event: any) {
    if (!this.loading) {
      this.offset += this.limit;
    }
    if (this.isLoadedLess) {
      (event as InfiniteScrollCustomEvent).target.complete();
      this.isNoMoreData = true;
      return;
    }

    this.ordersService
      .getOrdersList(
        this.limit,
        this.offset,
        this.loadingIds,
        this.ordersListData.statuses,
        'orders-list'
      )
      .pipe(
        take(1),
        finalize(() => {
          (event as InfiniteScrollCustomEvent).target.complete();
        })
      )
      .subscribe((res: OrdersResultDto) => {
        this.pushOrderToList(res.data.requests);
        this.isLoadedLess = res.data.requests.length < this.limit;
        this.isNoMoreData = res.data.requests.length === 0;
      });
  }

  reloadList(event?: any) {
    this.offset = 0;
    this.loading = !event;
    this.isRefresherDisabled = !event;

    this.ordersService
      .getOrdersList(
        this.limit,
        this.offset,
        this.loadingIds,
        this.ordersListData.statuses,
        'orders-list'
      )
      .pipe(
        take(1),
        finalize(() => {
          event?.target?.complete();
          this.loading = false;
          this.isRefresherDisabled = false;
        })
      )
      .subscribe((res: OrdersResultDto) => {
        this.setOrderList(res.data.requests);
        this.isLoadedLess = res.data.requests.length < this.limit;
        this.totalCount.emit(res.data.total);
        this.noData = this.ordersItems.length === 0;
      });
  }

  setOrderList(data: Order[]) {
    let filteredData = [...data];
    if (this.tabName === 'archived-requests') {
      if (this.ordersListData.statuses.includes(Status.CLOSED)) {
        filteredData = filteredData.filter(el => el.status === Status.CLOSED);
      }
      if (this.ordersListData.statuses.includes(Status.COMPLETED)) {
        filteredData = filteredData.filter(
          el => el.status === Status.COMPLETED
        );
      }
    }
    this.ordersItems = filteredData;
  }

  pushOrderToList(data: Order[]) {
    let filteredData = [...data];
    if (this.tabName === 'archived-requests') {
      if (this.ordersListData.statuses.includes(Status.CLOSED)) {
        filteredData = filteredData.filter(el => el.status === Status.CLOSED);
      }
      if (this.ordersListData.statuses.includes(Status.COMPLETED)) {
        filteredData = filteredData.filter(
          el => el.status === Status.COMPLETED
        );
      }
    }
    this.ordersItems.push(...filteredData);
  }

  setLoadingStatus(status: boolean) {
    this.loading = status;
  }
}
